import { createContext, useCallback, useContext, useState } from 'react';

import { AppMessage } from '@autopass-web/mochi-component';

export type AppContextProps = {
  onToastSuccess: (message: string) => void;
  onToastError: (message: string) => void;
};

export const AppContext = createContext<AppContextProps | null>(null);

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppContextProvider = ({ children }: AppProviderProps) => {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'failure';
  } | null>(null);

  const handleToastSuccess = useCallback((message: string) => {
    setToast({ message, type: 'success' });
  }, []);

  const handleToastError = useCallback((message: string) => {
    setToast({ message, type: 'failure' });
  }, []);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <AppContext.Provider
      value={{
        onToastError: handleToastError,
        onToastSuccess: handleToastSuccess,
      }}
    >
      <AppMessage
        show={toast != null}
        message={toast?.message ?? ''}
        onDismiss={handleToastClose}
        type={toast?.type}
        autoDismiss
      />
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }

  return context;
};
